import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../styles';
import * as stores from '../../stores';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import {
  DISCOUNT_PACKAGE_IDENTIFIER,
  findDiscountPackage,
  findPackageByType,
  getOfferTrialDays,
} from '../../utils/subscriptionUtils';

interface SubscriptionCardProps {
  onManageSubscription: () => void;
  onSeePlans: () => void;
}

const SubscriptionCard: FC<SubscriptionCardProps> = ({ onManageSubscription, onSeePlans }) => {
  const { t } = useTranslation();
  const offerings = stores.Purchase.useState((s) => s.offerings);
  const {
    isSubscribed,
    isTrialPeriod,
    hasBillingIssue,
    planType,
    price,
    trialDaysLeft,
    trialDaysLeftText,
    formattedDate,
  } = useSubscriptionStatus();

  // Derive total trial days dynamically from the active offering (mirrors PaymentModalByPreference)
  const earlyTargetOffering = offerings?.all?.['weekly_yearly_pricing'] || offerings?.current;
  const oneTimeDiscountOffering = offerings?.all?.[DISCOUNT_PACKAGE_IDENTIFIER];
  const discountPackage =
    oneTimeDiscountOffering?.annual ||
    oneTimeDiscountOffering?.availablePackages?.find((p) => p.packageType === 'ANNUAL') ||
    findDiscountPackage(earlyTargetOffering) ||
    null;
  const annualPackage = findPackageByType(earlyTargetOffering, 'ANNUAL');
  const trialTotalDays = getOfferTrialDays(discountPackage || annualPackage, 7);

  const progressPercent = Math.max(0, Math.min(100, ((trialTotalDays - trialDaysLeft) / trialTotalDays) * 100));

  const cardStyle = css`
    position: relative;
    background: ${colors.snow};
    border: 0.5px solid ${colors.cloud};
    border-radius: 8px;
    padding: ${spacing.deci};
    margin-top: ${spacing.deci};

    html[data-theme='dark'] & {
      background: #242424;
      border-color: ${colors.tungsten}50;
    }
  `;

  // State 1: Free Trial User
  if (isSubscribed && isTrialPeriod) {
    return (
      <div css={cardStyle}>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${spacing.centi};
          `}
        >
          <p
            css={css`
              ${fontStyles.lion};
              color: ${colors.liquorice};
              margin: 0;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {t('subscription.freeTrialToBloomPro')}
          </p>
          <span
            css={css`
              ${fontStyles.cat};
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {trialDaysLeftText}
          </span>
        </div>

        <div
          css={css`
            width: 100%;
            height: 4px;
            background: ${colors.cloud};
            border-radius: 2px;
            margin-bottom: ${spacing.centi};
            overflow: hidden;
            html[data-theme='dark'] & {
              background: ${colors.tungsten}50;
            }
          `}
        >
          <div
            css={css`
              height: 100%;
              width: ${progressPercent}%;
              background: ${colors.petrolBlue};
              border-radius: 2px;
              html[data-theme='dark'] & {
                background: ${colors.skyBlue};
              }
            `}
          />
        </div>

        <p
          css={css`
            ${fontStyles.cat};
            color: ${colors.silver};
            margin: 0 0 ${spacing.deci} 0;
            html[data-theme='dark'] & {
              color: ${colors.cloud};
            }
          `}
        >
          {formattedDate
            ? t('subscription.endsDateThenPrice', { date: formattedDate, price })
            : t('subscription.endingSoonThenPrice', { price })}
        </p>

        <button
          onClick={onManageSubscription}
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: none;
            border: none;
            border-top: 1px solid ${colors.cloud};
            padding: ${spacing.centi} 0 0 0;
            cursor: pointer;
            ${fontStyles.fatCat};
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
              border-color: ${colors.tungsten}50;
            }
          `}
        >
          {t('subscription.manageSubscription')}
        </button>
      </div>
    );
  }

  // State 2: Billing Issue (grace period — subscription still active but payment failed)
  if (isSubscribed && !isTrialPeriod && hasBillingIssue) {
    const planLabel = planType === 'yearly' ? t('subscription.yearly') : t('subscription.weekly');

    return (
      <div css={cardStyle}>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: ${spacing.centi};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.centi};
            `}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              css={css`
                flex-shrink: 0;
              `}
            >
              <circle cx="9" cy="9" r="9" fill={colors.earth} />
              <text x="9" y="13" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="bold">
                !
              </text>
            </svg>
            <span
              css={css`
                ${fontStyles.lion};
                color: ${colors.liquorice};
                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {t('subscription.bloomPro')}
            </span>
          </div>
          <span
            css={css`
              ${fontStyles.cat};
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {planLabel}
          </span>
        </div>

        <p
          css={css`
            ${fontStyles.cat};
            color: ${colors.earth};
            margin: 0 0 ${spacing.deci} 0;
            html[data-theme='dark'] & {
              color: ${colors.snow};
            }
          `}
        >
          {formattedDate ? t('subscription.expiresDate', { date: formattedDate }) : t('subscription.expiringSoon')}{' '}
          {t('subscription.updatePaymentMethod')}
        </p>

        <button
          onClick={onManageSubscription}
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: none;
            border: none;
            border-top: 1px solid ${colors.cloud};
            padding: ${spacing.centi} 0 0 0;
            cursor: pointer;
            ${fontStyles.fatCat};
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
              border-color: ${colors.tungsten}50;
            }
          `}
        >
          {t('subscription.manageSubscription')}
        </button>
      </div>
    );
  }

  // State 3: Active Subscriber (Yearly or Weekly)
  if (isSubscribed && !isTrialPeriod) {
    const planLabel = planType === 'yearly' ? t('subscription.yearly') : t('subscription.weekly');

    return (
      <div css={cardStyle}>
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.centi};
            `}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              css={css`
                flex-shrink: 0;
                path {
                  stroke: #ffffff !important;
                }
              `}
            >
              <circle cx="9" cy="9" r="9" fill={colors.leaf} />
              <path
                d="M5.5 9L8 11.5L12.5 6.5"
                fill="none"
                stroke="#ffffff"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              css={css`
                ${fontStyles.lion};
                color: ${colors.liquorice};
                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {t('subscription.subscribedToBloomPro')}
            </span>
          </div>
          <span
            css={css`
              ${fontStyles.cat};
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {planLabel}
          </span>
        </div>

        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <p
            css={css`
              ${fontStyles.cat};
              color: ${colors.silver};
              margin: 0;
              html[data-theme='dark'] & {
                color: ${colors.cloud};
              }
            `}
          >
            {formattedDate
              ? t('subscription.renewsDate', { date: formattedDate })
              : t('subscription.renewsAutomatically')}
          </p>
        </div>

        <button
          onClick={onManageSubscription}
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: none;
            border: none;
            border-top: 1px solid ${colors.cloud};
            padding: ${spacing.centi} 0 0 0;
            cursor: pointer;
            ${fontStyles.fatCat};
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
              border-color: ${colors.tungsten}50;
            }
          `}
        >
          {t('subscription.manageSubscription')}
        </button>
      </div>
    );
  }

  // State 3: Non-Subscriber
  return (
    <div css={cardStyle}>
      <p
        css={css`
          ${fontStyles.lion};
          color: ${colors.liquorice};
          margin: 0 0 ${spacing.micro} 0;
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {t('subscription.subscribeToBloomPro')}
      </p>

      <p
        css={css`
          ${fontStyles.thinFlea};
          color: ${colors.silver};
          margin: 0 0 ${spacing.deci} 0;
          html[data-theme='dark'] & {
            color: ${colors.cloud};
          }
        `}
      >
        {t('subscription.proFeaturesList')}
      </p>

      <button
        onClick={onSeePlans}
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          background: ${colors.petrolBlue};
          border: none;
          border-radius: 8px;
          padding: 10px ${spacing.deci};
          cursor: pointer;
          ${fontStyles.fatCat};
          color: ${colors.sugar};
          html[data-theme='dark'] & {
            background: ${colors.skyBlue};
            color: ${colors.liquorice};
          }
          &:hover {
            opacity: 0.9;
          }
          &:active {
            opacity: 0.9;
          }
        `}
      >
        {t('subscription.seePlans')}
      </button>
    </div>
  );
};

export default SubscriptionCard;
